import React from 'react';
import { motion } from 'framer-motion';
import heroImage from '../assets/hero_ai_neural.png';
import studentsImage from '../assets/students_ai_lab.png';

const textVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: 'easeOut' },
  }),
};

export default function HeroSection({ onExpertClick }) {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0B1C4E 0%, #122266 40%, #1a2d7a 70%, #0B1C4E 100%)',
      }}
    >
      {/* === RIGHT SIDE: Real AI Image === */}
      <div className="absolute right-0 top-0 w-full lg:w-[55%] h-full">
        {/* Main hero image */}
        <img
          src={heroImage}
          alt="AI Neural Network Visualization"
          className="w-full h-full object-cover"
          style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 30%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 30%)' }}
        />
        {/* Gradient fade from left so text stays readable */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, #0B1C4E 0%, #0B1C4E 10%, rgba(11,28,78,0.7) 35%, rgba(11,28,78,0.1) 70%, transparent 100%)',
          }}
        />
        {/* Bottom fade */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, #0B1C4E 0%, transparent 30%)',
          }}
        />
      </div>

      {/* === Floating decorative orbs === */}
      <div className="absolute top-1/3 left-[38%] w-48 h-48 rounded-full bg-royal-gold/10 blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 left-[30%] w-32 h-32 rounded-full bg-blue-400/10 blur-2xl animate-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />

      {/* === Animated dot grid overlay === */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #C9A84C 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* === HERO CONTENT === */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <div className="max-w-2xl">

          {/* Badge */}
          <motion.div
            custom={0}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-royal-gold/40 text-royal-gold-light text-sm font-semibold px-4 py-2 rounded-full mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-royal-gold animate-pulse" />
            AI-Powered Academic Excellence
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            custom={1}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
          >
            Elevate Your{' '}
            <br />
            <span
              style={{
                background: 'linear-gradient(90deg, #C9A84C 0%, #F0D080 50%, #C9A84C 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shimmer 3s linear infinite',
              }}
            >
              Academic
            </span>{' '}
            Journey
            <br />
            with{' '}
            <span className="text-royal-gold-light">AI</span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            custom={2}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="font-inter text-lg text-white/80 leading-relaxed mb-6"
          >
            Premium projects, research papers & cutting-edge AI services for{' '}
            <span className="text-royal-gold-light font-semibold">BE • BTech • MTech • BSc • Diploma • MCA • MBA • PhD</span>{' '}
            students across all technology domains.
          </motion.p>

          {/* Feature pills */}
          <motion.div
            custom={3}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap gap-2 mb-10"
          >
            {['AI & Machine Learning', 'IoT Systems', 'Cloud Computing', 'Research Papers', 'PhD Projects'].map((tag) => (
              <span
                key={tag}
                className="bg-white/10 border border-white/20 text-white/90 text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div
            custom={4}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap gap-4"
          >
            <motion.a
              href="#services"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(201,168,76,0.6)' }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 font-inter font-bold px-8 py-4 rounded-full text-royal-navy text-base"
              style={{ background: 'linear-gradient(135deg, #C9A84C 0%, #F0D080 50%, #C9A84C 100%)', boxShadow: '0 4px 20px rgba(201,168,76,0.35)' }}
            >
              Explore Services
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </motion.a>
            <motion.a
              href="#contact"
              onClick={(e) => { e.preventDefault(); onExpertClick?.(); }}
              whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.15)' }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-inter font-semibold px-8 py-4 rounded-full backdrop-blur-sm transition-all duration-300 text-base cursor-pointer"
            >
              Talk to Expert
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </motion.a>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            custom={5}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="mt-14 flex flex-wrap items-center gap-0"
          >
            {[
              { value: '5000+', label: 'Students Served' },
              { value: '200+', label: 'Technologies' },
              { value: '98%', label: 'Success Rate' },
            ].map((stat, i) => (
              <div key={stat.label} className="flex items-center">
                <div className="px-6 first:pl-0">
                  <p className="font-playfair text-2xl font-bold text-royal-gold-light">{stat.value}</p>
                  <p className="font-inter text-xs text-white/60">{stat.label}</p>
                </div>
                {i < 2 && <div className="w-px h-10 bg-white/20" />}
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* === Floating student image card (bottom right) === */}
      <motion.div
        initial={{ opacity: 0, y: 40, x: 20 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-10 right-8 hidden xl:block z-20"
      >
        <div
          className="rounded-2xl overflow-hidden shadow-2xl border border-white/20"
          style={{ width: 260, backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.08)' }}
        >
          <img
            src={studentsImage}
            alt="Students with AI"
            className="w-full h-36 object-cover"
          />
          <div className="p-3">
            <p className="font-inter text-white font-semibold text-sm">Students achieving excellence</p>
            <p className="font-inter text-white/60 text-xs mt-0.5">With AI-powered project support</p>
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-white/40 text-xs font-inter tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1"
        >
          <div className="w-1 h-2 bg-royal-gold rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}
